import * as React from 'react'
// We need to import this directly as the other hooks within the hooks folder
// are not SSR safe and causes the server to bundle these in.
import { useCookieConsentDecision } from 'hooks/useCookieConsentDecision'
import type {
  Cookie,
  CookiesPolicyProps,
  CookieSettingsLink
} from './CookiePolicy.types'

const storeTrackReason = 'to store and track visits across websites.'

const cookieTypes = {
  marketing: 'Marketing/ Tracking',
  functional: 'Functional',
  analytical: 'Analytical',
  statistics: 'Statistics'
}

const cookies: Cookie[] = [
  {
    company: 'Microsoft',
    cookieName: 'Microsoft Bing Ads _uetvid',
    type: cookieTypes.marketing,
    duration: '1 year',
    purpose: storeTrackReason
  },
  {
    company: 'Microsoft',
    cookieName: 'Microsoft Clarity _clsk',
    type: cookieTypes.marketing,
    duration: '1 day',
    purpose:
      'to store and combine pageviews by a user into a single session recording.'
  },
  {
    company: 'Microsoft',
    cookieName: 'Microsoft Bing Ads _uetsid',
    type: cookieTypes.marketing,
    duration: '1 day',
    purpose: storeTrackReason
  },
  {
    company: 'Laced',
    cookieName: 'rails_laced_app_session',
    type: cookieTypes.functional,
    duration: 'Session',
    purpose:
      'to store and track visits across websites. Are they on mobile device, cart number, Guest ID, Session ID, Checkout ID, Search'
  },
  {
    company: 'Laced',
    cookieName: '_laced_as',
    type: cookieTypes.analytical,
    duration: 'Session',
    purpose:
      'to identify product searches made in the same session. Used to improve search results ordering.'
  },
  {
    company: 'Laced',
    cookieName: '_laced_asqp',
    type: cookieTypes.analytical,
    duration: 'Session',
    purpose:
      'to track the position of selected products from the search results page. Used to improve search results ordering.'
  },
  {
    company: 'Microsoft',
    cookieName: 'Microsoft Clarity _clck',
    type: cookieTypes.marketing,
    duration: '1 year',
    purpose: 'to store a unique user ID.'
  },
  {
    company: 'Google',
    cookieName: 'Google Analytics _gid',
    type: cookieTypes.analytical,
    duration: '1 day',
    purpose: 'to store and count pageviews.'
  },
  {
    company: 'Stripe',
    cookieName: '__stripe_mid',
    type: cookieTypes.functional,
    duration: '1 year',
    purpose: 'to provide fraus prevention.'
  },
  {
    company: 'facebook',
    cookieName: '_fbp',
    type: cookieTypes.marketing,
    duration: '3 months',
    purpose: storeTrackReason
  },
  {
    company: 'Google',
    cookieName: 'Google Analytics _ga',
    type: cookieTypes.statistics,
    duration: '1 year',
    purpose:
      'This cookie is a Google Analytics persistent cookie which is used to distinguish unique users.'
  },
  {
    company: 'Google',
    cookieName: 'Google Adsense  _gcl_au',
    type: cookieTypes.marketing,
    duration: '1 year',
    purpose: 'to store and track conversions.'
  },
  {
    company: 'Laced',
    cookieName: '',
    type: '',
    duration: '',
    purpose: 'Cookie preference'
  }
]

const cookieSettingsLinks: CookieSettingsLink[] = [
  {
    label: 'Microsoft Edge',
    href: 'https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09'
  },
  {
    label: 'Chrome',
    href: 'https://support.google.com/chrome/answer/95647?hl=en'
  },
  {
    label: 'Firefox',
    href: 'https://support.mozilla.org/en-US/kb/delete-cookies-remove-info-websites-stored'
  },
  {
    label: 'Safari',
    href: 'https://support.apple.com/en-gb/guide/safari/sfri11471/mac'
  }
]

const CookiesPolicy: React.FC<CookiesPolicyProps> = ({
  siteHostname,
  stylizedHostname
}) => {
  const { mutate, existingCookiePreferences: preferences } =
    useCookieConsentDecision()

  return (
    <div className="layout legal">
      <h1>Cookies Policy</h1>
      <p>
        Our website,{' '}
        <a
          href={`https://${siteHostname}/`}
          target="_blank"
          rel="noreferrer noopener"
        >
          {stylizedHostname}
        </a>{' '}
        (our &quot;site&quot;) uses cookies to distinguish you from other users
        of our site. This helps us to provide you with a good experience when
        you browse our site and also allows us to improve our site. By
        continuing to browse our site, you are agreeing to our use of cookies.
      </p>

      <h4>What is a cookie?</h4>
      <p>
        A cookie is a small file of letters and numbers that we store on your
        browser or the hard drive of your computer if you agree. Cookie may be
        present only for the duration of your visit to our site or they may be
        persistent for the duration of your internet usage or of longer duration
        where they relate to providing a customised browsing experience of
        service for you on our site.
      </p>

      <h4>What cookies do we use?</h4>
      <p>
        We use the following cookies on our site for the purposes described:
      </p>
      <div className="legal-table-wrapper">
        <table className="legal-table caption">
          <thead>
            <tr>
              <td>Company</td>
              <td>Cookie name</td>
              <td>Type</td>
              <td>Duration</td>
              <td>Purpose</td>
            </tr>
          </thead>
          <tbody>
            {cookies.map((cookie) => (
              <tr key={cookie.cookieName}>
                <td>{cookie.company}</td>
                <td>{cookie.cookieName}</td>
                <td>{cookie.type}</td>
                <td>{cookie.duration}</td>
                <td>{cookie.purpose}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        You can change your{' '}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          href=""
          onClick={(e) => {
            e.preventDefault()
            mutate({ ...preferences, valid: false }, { revalidate: false })
          }}
        >
          cookies preference
        </a>{' '}
        on our site anytime.
      </p>
      <p>
        Most internet browsing software will allow you to control the acceptance
        and storage of cookies. You block cookies by activating the setting on
        your browser menu that allows you to refuse the setting for all or some
        cookies.
      </p>
      <p>
        To change your cookie settings in your web browser, please use the
        following links:
      </p>
      <ul className="legal-disc-list">
        {cookieSettingsLinks.map((link) => (
          <li key={link.href}>
            For{' '}
            <a href={link.href} target="_blank" rel="noreferrer noopener">
              {link.label}
            </a>
          </li>
        ))}
      </ul>
      <p>
        For further information about cookies, their control or management
        please see the{' '}
        <a
          href="https://ico.org.uk/for-the-public/online/cookies/"
          target="_blank"
          rel="noreferrer noopener"
        >
          Information Commissioner&apos;s Office guidance
        </a>
        .
      </p>
      <p className="legal-timestamp footnote dark">
        This Cookie Policy was last updated 18th May 2022.
      </p>
    </div>
  )
}

export default CookiesPolicy
